import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, App as AntdApp, Button, Tag } from 'antd';

import { DataState, Skeleton, deriveDataState } from '../loading';
import { listPackageVersions, publishingRefusal, withdrawPackageVersion } from '../../services/publishing';
import { apiErrorMessage } from '../../utils/apiErrors';
import { formatDisplayDateTime } from '../../utils/dateDisplay';
import { normalizeId } from '../../utils/ids';
import { versionStateLabel, versionTitle, versionWhyWords } from './publishingWords';

const PAGE = 50;

/** "Sent 27 Oct 2026, 10:00 by Abe · Decided 28 Oct 2026, 09:12" — the maker only when the API names them. */
function metaLine(row) {
	const parts = [];
	if (row.submittedAt) {
		const by = row.mine ? ' by you' : row.madeBy?.name ? ` by ${row.madeBy.name}` : '';
		parts.push(`Sent ${formatDisplayDateTime(row.submittedAt)}${by}`);
	}
	if (row.decidedAt && (row.state === 'approved' || row.state === 'refused')) parts.push(`Decided ${formatDisplayDateTime(row.decidedAt)}`);
	return parts.join(' · ');
}

/**
 * The Versions section of a publisher's package page (keepr-api KPR-1327):
 * every version, newest first — waiting, listed, approved, not approved with
 * the reviewer's reason, withdrawn, replaced — and **Withdraw…** on the
 * caller's own version still waiting. A newest version not approved leads with
 * the reason and the way to a new one (Edit listing…, Rebuild…).
 *
 * `refreshKey` reads it again (the page bumps it whenever it reads the
 * package); `onChanged` tells the page a version was withdrawn.
 */
export default function PackageVersions({ packageId, refreshKey, canRebuild, onEditListing, onRebuild, onChanged }) {
	const { modal } = AntdApp.useApp();
	const [rows, setRows] = useState(null);
	const [total, setTotal] = useState(0);
	const [loading, setLoading] = useState(true);
	const [more, setMore] = useState(false);
	const [error, setError] = useState(null);
	const [notice, setNotice] = useState(null); // { type, text }
	const [busy, setBusy] = useState(null); // the version being withdrawn
	const requestRef = useRef(0);

	const load = useCallback(() => {
		const request = ++requestRef.current;
		setLoading(true);
		setError(null);
		listPackageVersions(packageId, { skip: 0, limit: PAGE })
			.then((answer) => {
				// An answer for another package, or one a newer read has overtaken, is dropped.
				if (request !== requestRef.current) return;
				setRows(answer.rows);
				setTotal(answer.total);
				setLoading(false);
			})
			.catch((e) => {
				if (request !== requestRef.current) return;
				setError(e);
				setLoading(false);
			});
	}, [packageId]);

	useEffect(() => {
		setRows(null);
		load();
		return () => { requestRef.current += 1; };
	}, [load, refreshKey]);

	const showMore = async () => {
		const request = requestRef.current;
		setMore(true);
		try {
			const answer = await listPackageVersions(packageId, { skip: rows.length, limit: PAGE });
			if (request !== requestRef.current) return;
			const seen = new Set(rows.map(r => r._id));
			setRows([...rows, ...answer.rows.filter(r => !seen.has(r._id))]);
			setTotal(answer.total);
		} catch (e) {
			if (request === requestRef.current) setNotice({ type: 'error', text: apiErrorMessage(e, 'The older versions could not be read.') });
		} finally {
			setMore(false);
		}
	};

	const withdraw = row => modal.confirm({
		title: `Withdraw version ${row.version || 1}?`,
		content: 'It leaves the review queue and nothing is listed. You can send it again later.',
		okText: 'Withdraw',
		onOk: async () => {
			setBusy(row._id);
			try {
				await withdrawPackageVersion(packageId, row._id);
				setNotice({ type: 'success', text: `Withdrew version ${row.version || 1}.` });
				if (onChanged) onChanged();
				else load();
			} catch (e) {
				setNotice({ type: 'error', text: publishingRefusal(e) || apiErrorMessage(e, 'The version could not be withdrawn.') });
				if (e?.response?.data?.code === 'review_closed') load();
			} finally {
				setBusy(null);
			}
		},
	});

	const list = rows || [];
	const newest = list[0];
	const refused = newest && newest.state === 'refused' ? newest : null;

	return (
		<section className="publishing-package__section" aria-labelledby="publishing-versions">
			<h2 id="publishing-versions" className="publishing-package__heading">Versions</h2>
			{notice ? (
				<Alert
					type={notice.type}
					showIcon
					closable
					onClose={() => setNotice(null)}
					message={notice.text}
					className="publishing-package__notice"
					role={notice.type === 'error' ? 'alert' : 'status'}
				/>
			) : null}
			{refused ? (
				<Alert
					type="warning"
					showIcon
					role="region"
					aria-label="Not approved"
					className="publishing-versions__refused"
					message={`${versionTitle(refused)} was not approved`}
					description={(
						<>
							{refused.reason ? <p className="publishing-versions__reason"><span className="publishing-versions__reason-label">The reviewer&apos;s reason: </span>{refused.reason}</p> : null}
							<p>
								{refused.change === 'publish'
									? 'Change what the reviewer named, then publish again.'
									: 'Change what the reviewer named, then send a new version.'}
							</p>
							<div className="publishing-versions__next">
								{onEditListing ? <Button onClick={onEditListing}>Edit listing…</Button> : null}
								{canRebuild && onRebuild ? <Button onClick={onRebuild}>Rebuild…</Button> : null}
							</div>
						</>
					)}
				/>
			) : null}
			<DataState
				state={deriveDataState({ loading: loading && rows === null, error: rows === null ? error : null, hasData: rows !== null, isEmpty: rows !== null && rows.length === 0 })}
				hasData={rows !== null}
				isEmpty={rows !== null && rows.length === 0}
				skeleton={<Skeleton variant="list-row" count={2} />}
				empty={<p className="publishing-package__quiet">No versions yet. Publish sends the first one for review.</p>}
				error={error ? 'Check your connection and try again.' : null}
				onRetry={load}
				label="its versions"
			>
				<ul className="publishing-versions" aria-label="Versions">
					{list.map(row => (
						<li key={normalizeId(row._id) || row._id} className="publishing-versions__row">
							<div className="publishing-versions__head">
								<span className="publishing-versions__title">{versionTitle(row)}</span>
								<Tag className="publishing-versions__state">{versionStateLabel(row)}</Tag>
							</div>
							<div className="publishing-versions__meta">{metaLine(row)}</div>
							{row.state === 'refused' && row.reason && row !== refused ? (
								<p className="publishing-versions__reason"><span className="publishing-versions__reason-label">The reviewer&apos;s reason: </span>{row.reason}</p>
							) : null}
							{versionWhyWords(row) ? <div className="publishing-versions__why">{versionWhyWords(row)}</div> : null}
							{row.canWithdraw ? (
								<div className="publishing-versions__acts">
									<Button onClick={() => withdraw(row)} loading={busy === row._id}>Withdraw…</Button>
								</div>
							) : null}
							{row.state === 'held' && !row.canWithdraw ? (
								<div className="publishing-versions__why">Only the account that sent it can withdraw it.</div>
							) : null}
						</li>
					))}
				</ul>
				{list.length < total ? (
					<Button className="publishing-versions__more" onClick={showMore} loading={more}>Show older versions</Button>
				) : null}
			</DataState>
		</section>
	);
}
